import { useState } from "react";
import type { PhotoLocation, DMVLocation, AirportLocation, VitalRecordsLink, Landmark } from "../types";

export default function CityHelpfulResourcesSection({
  city,
  state,
  photoLocations,
  dmvLocations,
  airports,
  dmvWebsite,
  dmvLabel,
  vitalRecordsLinks,
  landmarks,
}: {
  city: string;
  state?: string;
  photoLocations: PhotoLocation[];
  dmvLocations: DMVLocation[];
  airports: AirportLocation[];
  dmvWebsite?: string;
  dmvLabel?: string;
  vitalRecordsLinks?: VitalRecordsLink[];
  landmarks?: Landmark[];
}) {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  // Default to the U.S. DHS REAL ID page if no state-specific DMV link is provided
  const fallbackDmvUrl = "https://www.dhs.gov/real-id";
  const fallbackDmvText = "U.S. DHS — REAL ID Information";
  const dmvUrl = dmvWebsite || fallbackDmvUrl;
  const dmvLinkText = dmvLabel || fallbackDmvText;

  return (
    <section id="local-resources" className="bg-[#F7F8FA] pt-8 md:pt-10 pb-20 md:pb-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for {city} Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations in {city}</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular passport photo locations in {city} include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    {loc.website ? (
                      <a href={loc.website} target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] text-xs leading-snug mb-1 block hover:text-[#F6C344] transition-colors">
                        {loc.name} <i className="ri-external-link-line text-[10px] ml-0.5"></i>
                      </a>
                    ) : (
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    )}
                    {loc.website ? (
                      <a href={loc.website} target="_blank" rel="noopener noreferrer" className="text-gray-500 text-xs leading-relaxed hover:text-[#1A365D] transition-colors block">
                        {loc.address}
                      </a>
                    ) : (
                      <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    )}
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && photoLocations.length > 6 && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {vitalRecordsLinks && vitalRecordsLinks.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                  <i className="ri-file-text-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                  <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — {city}</p>
                </div>
              </div>
              <div className="px-6 py-6">
                <p className="text-gray-500 text-sm leading-relaxed mb-5">
                  If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records for {city} residents. Most records can be ordered online for delivery within 5–10 business days.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  {vitalRecordsLinks.map((record, i) => (
                    <a
                      key={i}
                      href={record.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                    >
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                        <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                        <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                        <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                          <i className="ri-external-link-line text-xs"></i>Get Records
                        </span>
                      </div>
                    </a>
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-gray-600 text-xs leading-relaxed">
                    <i className="ri-information-line text-amber-500 mr-1.5"></i>
                    <strong>Don&apos;t see your county?</strong> If the record you need was issued outside the area, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">DMV Services Near {city}</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed driver&apos;s license or REAL ID, here are the DMV service locations near {city}.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href={dmvUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>{dmvLinkText}
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online before visiting.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving {city} Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                {city} travelers use nearby airports for domestic and international travel. If your trip is coming up fast, make sure your passport is in hand well before your departure date.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {airports.map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>

          {landmarks && landmarks.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                  <i className="ri-landscape-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Nearby Landmarks</p>
                  <p className="text-white font-bold text-base leading-tight mt-0.5">Key Landmarks &amp; Transportation Hubs Near {city}</p>
                </div>
              </div>
              <div className="px-6 py-6">
                <p className="text-gray-500 text-sm leading-relaxed mb-5">
                  {city} is surrounded by major airports, cruise ports, and attractions that define travel patterns for {state || "the area"} residents. These landmarks are the most common destinations and departure points for {city} travelers.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                  {landmarks.map((landmark, i) => (
                    landmark.url ? (
                      <a
                        key={i}
                        href={landmark.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                      >
                        <div className="flex items-center gap-2.5 mb-2.5">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 group-hover:bg-[#F6C344]/15 transition-colors">
                            <i className={`${landmark.icon} text-[#1A365D] text-sm`}></i>
                          </div>
                          <div>
                            <p className="font-bold text-[#1A365D] text-xs leading-snug">{landmark.name}</p>
                            <p className="text-gray-400 text-[11px]">{landmark.distance}</p>
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs leading-relaxed">{landmark.desc}</p>
                      </a>
                    ) : (
                      <div
                        key={i}
                        className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center gap-2.5 mb-2.5">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                            <i className={`${landmark.icon} text-[#1A365D] text-sm`}></i>
                          </div>
                          <div>
                            <p className="font-bold text-[#1A365D] text-xs leading-snug">{landmark.name}</p>
                            <p className="text-gray-400 text-[11px]">{landmark.distance}</p>
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs leading-relaxed">{landmark.desc}</p>
                      </div>
                    )
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="text-gray-600 text-xs leading-relaxed">
                    <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                    <strong>Passport required for all international travel.</strong> Whether you are departing from a nearby international airport, boarding a cruise, or traveling abroad — a valid U.S. passport is mandatory. <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">Expedited processing through Fast Passport Center</a> is the fastest option for {city} residents.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}